import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { getBidHistory } from '@/redux/modules/bidHistory';
import { getCountDown } from '@/redux/modules/bidCountDown';
import { getItemDetailExternalUrl } from '@/redux/modules/itemDetail';
import { getItemModelCatalogStatus, getItemSummary } from '@/redux/modules/item/summary/itemSummary.selectors';
import { getLiveItemStatusOpen } from '@/redux/modules/liveItemStatus';
import { getLoadTimeForBidIncrements } from '@/redux/modules/bidIncrements/bidIncrements.selectors';
import { getRegistrationApproved } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { isAfterStartTimeOnStartDay } from '@/utils/isAfterStartTimeOnStartDay';
import { isUserLoggedIn } from '@/redux/modules/account/user/user.selectors';
import { openSendSellerMessageModal, openWaitingForApprovalModal } from '@/redux/modules/modal';
import { submitRegisterForCatalog } from '@/redux/modules/registerForCatalog';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import BidCountDown from '@/components/BidCountDown/BidCountDown';
import BiddingConsoleButton from '@/pages/BiddingConsole/components/BiddingConsoleButton/BiddingConsoleButton';
import BidHistoryTable from '@/components/BidHistoryTable/BidHistoryTable';
import BidMessage, { BidMessageModalOptions } from '@/components/BidMessage/BidMessage';
import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import ItemBiddingAbsenteeExperiment from '../../SimpleBidBoxExperiment/ItemBiddingAbsenteeExperiment';
import ItemBiddingEnterButton from '@/components/ItemBidding/ItemBiddingEnterButton';
import ItemBiddingSellSimilarButton from '@/components/ItemBidding/ItemBiddingSellSimilarButton';
import ItemBiddingStatusRow from '@/components/ItemBidding/ItemBiddingStatusRow';
import React from 'react';
import RequestOnlineBiddingButton from '@/components/ItemBidding/RequestOnlineBiddingButton';
import styled from 'styled-components';

export type OpenForBiddingProps = {
    itemId: number;
};

const OpenForBidding = ({ itemId }: OpenForBiddingProps) => {
    const { formatMessage } = useIntl();
    const dispatch = useAppDispatch();

    const {
        catalogId,
        currency,
        isCatalogOnly,
        isLocked,
        isPassed,
        isSold,
        isTimedAuction,
        isTimedPlusAuction,
        lotNumber,
        saleStartTs,
        sellerId,
        sellerName,
        startPrice,
        title,
    } = useAppSelector((state) => getItemSummary(state, itemId));

    const approved = useAppSelector((state) => getRegistrationApproved(state, catalogId));
    const externalUrl = useAppSelector((state) => getItemDetailExternalUrl(state, itemId));
    const isLotOpen = useAppSelector((state) => getLiveItemStatusOpen(state, itemId));
    const timeStamp = useAppSelector((state) => getCountDown(state, itemId));
    const userLoggedIn = useAppSelector(isUserLoggedIn);
    const bidHistory = useAppSelector((state) => getBidHistory(state, itemId));
    const catalogStatus = useAppSelector((state) => getItemModelCatalogStatus(state, itemId));
    const bidIncrementLoadTime = useAppSelector((state) => getLoadTimeForBidIncrements(state, catalogId));
    const bidIncrementsLoaded = Number(bidIncrementLoadTime) && bidIncrementLoadTime > 0;

    const [showBidHistory, setShowBidHistory] = React.useState(false);
    const toggleBidHistory = React.useCallback(() => setShowBidHistory(!showBidHistory), [showBidHistory]);

    const showCountDown = isTimedAuction && Boolean(timeStamp) && approved;

    const submitCatalogRegistration = React.useCallback(
        (attrs) => {
            dispatch(submitRegisterForCatalog(attrs));
        },
        [dispatch]
    );

    const showWaitingForApprovalModal = React.useCallback(() => {
        dispatch(openWaitingForApprovalModal(catalogId));
    }, [dispatch, catalogId]);

    const showSendSellerMessageModal = React.useCallback(
        (formatObject: BidMessageModalOptions) => {
            const messageDetails = {
                catalogId,
                itemId,
                lotNumber,
                title,
            };

            //'registration.issue'
            const subject = formatMessage({ id: formatObject.id }, formatObject.values);
            return dispatch(openSendSellerMessageModal(messageDetails, sellerId, sellerName, subject));
        },
        [catalogId, dispatch, formatMessage, itemId, lotNumber, sellerId, sellerName, title]
    );

    //required for shouldAddMargin below for proper styling.
    //Perhaps we won't need this later if the structure of the page is redone.
    const isUnsold = isLocked && !isPassed && !isSold && !isLotOpen;
    const showItemBiddingAbsentee = !isCatalogOnly && !isPassed && !isSold && !isUnsold;
    const showBiddingConsoleButton = isLotOpen && !isTimedPlusAuction;
    const showItemBiddingEnterButton = catalogStatus === 'live' && !isCatalogOnly && !isTimedPlusAuction;

    //StyledButtonContainer needs dynamic margin based on whether or not other buttons/elements
    //are showing because of how components are structured. The above are checks required for proper styling.
    const shouldAddMargin = showItemBiddingEnterButton && (showItemBiddingAbsentee || showBiddingConsoleButton);

    return (
        <StyledContent>
            <BidMessage
                itemId={itemId}
                showSendSellerMessageModal={showSendSellerMessageModal}
                showWaitingForApprovalModal={showWaitingForApprovalModal}
            />
            {((isAfterStartTimeOnStartDay(saleStartTs) && isCatalogOnly) ||
                (isCatalogOnly && !isSold && !isPassed)) && (
                <RequestOnlineBiddingButton
                    catalogId={catalogId}
                    itemId={itemId}
                    submitRegisterForCatalog={submitCatalogRegistration}
                    text={
                        externalUrl ? (
                            <FormattedMessage id="register_partners_site" />
                        ) : (
                            <FormattedMessage id="request_online_bidding" />
                        )
                    }
                />
            )}
            <ItemBiddingStatusRow itemId={itemId} />
            <StyledPrintOnlyContent>
                <FormattedMessage id="starting_bid" />{' '}
                <StyledFormattedCurrencyStart
                    convert
                    noFraction
                    sourceCurrency={currency}
                    value={startPrice}
                />
            </StyledPrintOnlyContent>

            <FlexContainer>
                {showBiddingConsoleButton ? (
                    <StyledButtonContainer $addMargin={shouldAddMargin}>
                        <BiddingConsoleButton
                            catalogId={catalogId}
                            itemId={itemId}
                            noLinks
                        />
                        {showCountDown && <BidCountDown timeStamp={timeStamp} />}
                    </StyledButtonContainer>
                ) : (
                    <StyledButtonContainer $addMargin={shouldAddMargin}>
                        {/* we need to wait until the bidIncrements are loaded first */}
                        {/* {Boolean(bidIncrementsLoaded) && <ItemBiddingAbsentee catalogId={catalogId} itemId={itemId} />} */}
                        {Boolean(bidIncrementsLoaded) && (
                            <ItemBiddingAbsenteeExperiment
                                catalogId={catalogId}
                                itemId={itemId}
                            />
                        )}
                    </StyledButtonContainer>
                )}

                {userLoggedIn && (isSold || isPassed) && (
                    <>
                        <BidHistoryTable
                            itemId={itemId}
                            showBidHistory={showBidHistory}
                        />
                        {bidHistory.length > 3 && ( // see above comment
                            <StyledToggleBidHistory onClick={toggleBidHistory}>
                                <BodySecondary
                                    className="underline"
                                    color="secondary"
                                >
                                    <FormattedMessage id={showBidHistory ? 'hide_bid_history' : 'show_bid_history'} />
                                </BodySecondary>
                            </StyledToggleBidHistory>
                        )}
                    </>
                )}
                <LayoutWrapper>
                    {!isTimedPlusAuction && <ItemBiddingEnterButton itemId={itemId} />}
                    <ItemBiddingSellSimilarButton itemId={itemId} />
                </LayoutWrapper>
            </FlexContainer>
        </StyledContent>
    );
};

export default OpenForBidding;

const StyledContent = styled.div`
    flex: 1 1 auto;
    padding: ${({ theme }) => theme.spacing.lg};
    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        padding: ${({ theme }) => theme.spacing.sm};
    }
    @media print {
        padding: 0;
    }

    & > a {
        display: block;
    }
    & > *:last-child {
        margin-bottom: 0;
    }
    > div:not(:first-child),
    a {
        @media print {
            display: none;
        }
    }
    > div:first-child {
        @media print {
            margin: 0;
        }
    }
`;

const StyledButtonContainer = styled.div<{ $addMargin: Boolean }>`
    margin-bottom: ${({ $addMargin }) => ($addMargin ? '8px' : '0px')};
`;

const FlexContainer = styled.div`
    gap: 8px;
`;

const LayoutWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const StyledPrintOnlyContent = styled.span`
    @media screen {
        display: none;
    }

    display: flex;
    justify-content: center;
    width: 100%;
    margin: 7px auto;
`;

const StyledFormattedCurrencyStart = styled(FormattedCurrency)`
    padding-left: 7px;
`;

const StyledToggleBidHistory = styled.div`
    text-align: center;
    margin: -15px 0 20px;
    cursor: pointer;
`;
